import dayjs from 'dayjs';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { ComponentList } from '../../components/core/component-list.js';
import { Component } from '../../components/core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { CustomerAccountBase } from './customer-account-base.js';

export class GuestAccount extends CustomerAccountBase {
  static readonly TYPE_ID: string = 'aa9b3472388f46c5a42cbaaaabadde03';

  public readonly formattedName: string;
  public readonly initials: string;

  constructor(
    public readonly uniqueId: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly email: string,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly components: ComponentList,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId() {
    return GuestAccount.TYPE_ID;
  }

  get hasAName(): boolean {
    return (this.firstName !== null && this.firstName.length !== 0) ||
          (this.lastName !== null && this.lastName.length !== 0);
  }

  static fromMap(context: SerializationContext, map: SerializedData): GuestAccount {
    return new GuestAccount(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'firstName'),
        MapUtils.get<string>(map, 'lastName'),
        MapUtils.get<string>(map, 'email'),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'firstName': this.firstName,
      'lastName': this.lastName,
      'email': this.email,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'creationDate': context.encodeDateOrNull(this.creationDate),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new GuestAccount(
        this.uniqueId,
        this.firstName,
        this.lastName,
        this.email,
        this.phoneNumber?.clone() ?? null,
        TimeZone.cloneDate(this.creationDate),
        this.components.clone(),
    ) as this;
  }

  isSame(other: GuestAccount): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        other.email === this.email &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        other.creationDate === this.creationDate &&
        other.components.isSame(this.components);
  }

  copyWith(fields?: {
      uniqueId?: string,
      firstName?: string,
      lastName?: string,
      email?: string,
      phoneNumber?: PhoneNumber,
      creationDate?: dayjs.Dayjs,
      components?: ComponentList,
    }): GuestAccount {
    return new GuestAccount(
        fields?.uniqueId ?? this.uniqueId,
        fields?.firstName ?? this.firstName,
        fields?.lastName ?? this.lastName,
        fields?.email ?? this.email,
        fields?.phoneNumber ?? this.phoneNumber,
        fields?.creationDate ?? (this.creationDate?.clone() ?? null),
        fields?.components ?? this.components.clone(),
    );
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent<C extends Component>(component: C): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof GuestAccount;
  }

  static assertType(dm: DataModel): dm is GuestAccount {
    return dm instanceof GuestAccount;
  }
}

export class GuestAccountBuilder extends DataModelBuilder<GuestAccount> {
  build(context: SerializationContext, map: SerializedData): GuestAccount {
    return GuestAccount.fromMap(context, map);
  }
}
